"use client";

import * as React from "react";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { Progress as ProgressPrimitive } from "radix-ui";
import { cn } from "@/lib/utils";

let loading = false;
let currentUrl = "";
const listeners = new Set<() => void>();

function setLoading(value: boolean) {
  loading = value;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return loading;
}

function getServerSnapshot() {
  return false;
}

function useOnNavigate() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isLoading = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );
  const urlRef = useRef(currentUrl);

  useEffect(() => {
    const url = pathname + searchParams.toString();

    if (urlRef.current !== url) {
      urlRef.current = url;
      currentUrl = url;
      if (loading) {
        setLoading(false);
      }
    }
  }, [pathname, searchParams]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");

      if (
        anchor &&
        anchor.href &&
        anchor.target !== "_blank" &&
        !anchor.href.startsWith("#") &&
        !anchor.href.startsWith("javascript:") &&
        anchor.origin === window.location.origin
      ) {
        const targetUrl = anchor.pathname + anchor.search;
        const current = window.location.pathname + window.location.search;

        if (targetUrl !== current) {
          setLoading(true);
        }
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  return isLoading;
}

export function LoadingBar() {
  const isLoading = useOnNavigate();
  const [progress, setProgress] = React.useState(0);
  const prevLoadingRef = useRef(isLoading);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;

    if (isLoading && !wasLoading) {
      const timer0 = setTimeout(() => setProgress(0), 0);
      const timer1 = setTimeout(() => setProgress(30), 100);
      const timer2 = setTimeout(() => setProgress(50), 300);
      const timer3 = setTimeout(() => setProgress(70), 600);
      const timer4 = setTimeout(() => setProgress(85), 1000);

      return () => {
        clearTimeout(timer0);
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
      };
    } else if (!isLoading && wasLoading) {
      const timer1 = setTimeout(() => setProgress(100), 0);
      const timer2 = setTimeout(() => setProgress(0), 200);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isLoading]);

  if (!isLoading && progress === 0) {
    return null;
  }

  return (
    <ProgressPrimitive.Root
      data-slot="loading-bar"
      className={cn(
        "fixed left-0 top-0 z-9999 h-0.5 w-full bg-transparent",
        "transition-opacity duration-200",
        isLoading || progress === 100 ? "opacity-100" : "opacity-0",
      )}
      value={progress}
    >
      <ProgressPrimitive.Indicator
        data-slot="loading-bar-indicator"
        className={cn(
          "bg-primary size-full transition-transform duration-300 ease-out",
          progress === 100 && "duration-100",
        )}
        style={{ transform: `translateX(-${100 - progress}%)` }}
      />
    </ProgressPrimitive.Root>
  );
}
